"use strict";

const {format} = require('util');
const {EventEmitter} = require('events');

function noop() {}
const NOOP_LOGGER = {
	assert: noop, dir: noop, error: noop, info: noop,
	log: noop, time: noop, timeEnd: noop, trace: noop,
	warn: noop
};

class ProcessError extends Error {
	constructor(message) {
		super();
		this.message = format.apply(null, arguments);
		this.name = this.constructor.name;
	}

	toString() {
		return this.message;
	}
}

class ProcessSignal {
	constructor(message) {
		this.message = format.apply(null, arguments);
		this.name = this.constructor.name;
	}

	toString() {
		return this.message;
	}
}

class ProcessPause extends ProcessSignal {}
class ProcessStop extends ProcessSignal {}
class ProcessEndIteration extends ProcessSignal {}

const RUNNING = new Set();

class IOProcess extends EventEmitter {
	constructor(options) {
		super();
		this._options = Object.assign({
			loopTimeout: 0.1,
			errorTimeout: 15000,
			runOnce: false,
			stopOnErrors: false
		}, options);

		this._stopped = false;
		this._running = false;
		this._schedule = null;

		this._bind('loop', 'next', 'stop', 'loopSchedule', '_stop');
	}

	_bind(... methods) {
		for (let m of methods) {
			Object.defineProperty(this, m, {value: this[m].bind(this), writable: false});
		}
	}

	get processID() {
		if (!this._processID) {
			let options = this._options;
			if (options.hasOwnProperty('processID')) {
				this._processID = options.processID;
			} else if (options.hasOwnProperty('name')) {
				this._processID = options.name;
			} else {
				this._processID = this.constructor.name;
			}
		}
		return this._processID;
	}

	get options() {
		return this._options;
	}

	get stopped() {
		return this._stopped;
	}

	get running() {
		return this._running;
	}

	get logger() {
		if (!this._logger) {
			let l = this.options.logger;
			if (l) {
				this._logger = l;
			} else {
				this._logger = NOOP_LOGGER;
			}
		}
		return this._logger;
	}

	run() {
		if (!this.running) {
			this.constructor.running.add(this);
			this._stopped = false;
			this._running = true;
			this.next(null, 1);
		}
	}

	stop(callback) {
		callback = callback || noop;
		let self = this;
		this._stopped = true;
		if (!this.running) {
			return callback();
		} else {
			let stopHandler = function stopHandler() {
				self.removeListener('stop', stopHandler);
				return callback();
			};
			this.on('stop', stopHandler);
			if (this._schedule) {
				clearTimeout(this._schedule);
				this._schedule = null;
			}
			setImmediate(this.next);
		}
	}

	loop() {
		this.next();
	}

	loopSchedule() {
		this._schedule = null;
		this.loop();
	}

	next(err, timeout) {
		this._schedule = null;
		if (this.stopped) {
			this._stop();
			return;
		}
		let options = this.options;

		if (err) {
			let {logger, processID} = this;
			if (!(err instanceof ProcessSignal)) {
				this.handleError(err);
				if (options.stopOnErrors) {
					logger.error(`Process ${processID} stopped because of error`);
					return this._stop(err);
				}
			} else {
				this.handleProcessSignal(err);
				if (err instanceof ProcessStop) {
					this.handleStop(err);
					return this._stop(err);
				}
				if (options.runOnce && (err instanceof ProcessEndIteration)) {
					this.handleEndIteration(err);
					return this._stop(err);
				}
			}
		}

		timeout = timeout || this.loopTimeout(err);
		if (timeout) {
			this._schedule = setTimeout(this.loopSchedule, timeout);
		} else {
			this._schedule = setImmediate(this.loopSchedule);
		}
	}

	handleError(err) {
		let message = err.message || String(err);
		let {logger, processID} = this;
		logger.error(`Process ${processID} caused error: ${message}`);
		let stack = err.stack;
		if (stack) {
			logger.error(stack);
		}
		this.emit('err', err);
	}

	handleProcessSignal(signal) {
		this.emit('process', signal);
	}

	handleEndIteration(signal) {
		let {logger, processID} = this;
		let message = signal.message || String(signal);
		logger.info(`Process ${processID} iteration complete: ${message}`);
	}

	handleStop(signal) {
		let {logger, processID} = this;
		let message = signal.message || String(signal);
		logger.info(`Process ${processID} stopped: ${message}`);
	}

	loopTimeout(err) {
		let options = this.options;
		if (!err) {
			return options.loopTimeout;
		} else {
			return options.errorTimeout;
		}
	}

	_stop(err) {
		this._stopped = true;
		this._running = false;
		this.constructor.running.delete(this);
		this.emit('stop', err);
	}

	static get running() {
		return RUNNING;
	}

	static stopAll(callback) {
		let processes = Array.from(this.running);
		function nextProcess() {
			let p = processes.shift();
			if (p) {
				p.stop(function (err) {
					if (err) {
						return callback(err);
					} else {
						setImmediate(nextProcess);
					}
				});
			} else {
				callback();
			}
		}
		nextProcess();
	}
}

module.exports = {
	IOProcess,
	ProcessError, ProcessSignal, ProcessStop, 
	ProcessPause, ProcessEndIteration
};
